import { useState } from 'react';

import type { FieldDefinition } from '../../../data/model';
import { MAX_DISPLAY_FILTER_CONDITIONS } from '../../../displayFilters/displayFilter';
import type { LayerId } from '../../../project/model';
import {
  attributeRuleConditionWithField,
  attributeRuleConditionWithOperator,
  createDefaultAttributeRuleCondition,
} from '../../../rules/editing';
import { attributeRuleField, attributeRuleFields } from '../../../rules/fields';
import type {
  AttributeRuleCondition,
  AttributeRuleGroup,
  AttributeRuleOperator,
} from '../../../rules/model';
import {
  attributeRuleOperatorDefinition,
  attributeRuleOperatorsForField,
} from '../../../rules/operators';
import { useCapability } from '../../../features/useCapability';
import { useAppStore } from '../../state/useAppStore';
import { fieldDisplayLabel } from '../../../joins/effectiveLayer';

interface DisplayFilterEditorProps {
  readonly fields: readonly FieldDefinition[];
  readonly filter: AttributeRuleGroup | null;
  readonly layerId: LayerId;
}

function conditionSummary(
  condition: AttributeRuleCondition,
  fields: readonly FieldDefinition[],
): string {
  const field = attributeRuleField(fields, condition.field);
  const operator = field ? attributeRuleOperatorDefinition(field, condition.operator) : null;
  const comparison = operator?.valueKind === 'none' ? '' : ` ${String(condition.value ?? '')}`;
  return `${field ? fieldDisplayLabel(field) : condition.field} ${operator?.label ?? condition.operator}${comparison}`;
}

function ConditionEditor({
  condition,
  fields,
  index,
  onChange,
  onRemove,
}: {
  readonly condition: AttributeRuleCondition;
  readonly fields: readonly FieldDefinition[];
  readonly index: number;
  readonly onChange: (condition: AttributeRuleCondition) => void;
  readonly onRemove: () => void;
}) {
  const field = attributeRuleField(fields, condition.field);
  if (!field) return null;
  const definition = attributeRuleOperatorDefinition(field, condition.operator);
  const number = index + 1;

  return (
    <div className="display-filter-condition">
      <select
        aria-label={`Display filter field ${number}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithField(condition, event.target.value, fields);
          if (next) onChange(next);
        }}
        value={condition.field}
      >
        {attributeRuleFields(fields).map((candidate) => (
          <option key={candidate.name} value={candidate.name}>
            {fieldDisplayLabel(candidate)}
          </option>
        ))}
      </select>
      <select
        aria-label={`Display filter operator ${number}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithOperator(
            condition,
            event.target.value as AttributeRuleOperator,
            fields,
          );
          if (next) onChange(next);
        }}
        value={condition.operator}
      >
        {attributeRuleOperatorsForField(field).map((operator) => (
          <option key={operator.operator} value={operator.operator}>
            {operator.label}
          </option>
        ))}
      </select>
      {definition?.valueKind === 'number' ? (
        <input
          aria-label={`Display filter value ${number}`}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (Number.isFinite(value)) onChange({ ...condition, value });
          }}
          type="number"
          value={typeof condition.value === 'number' ? condition.value : 0}
        />
      ) : definition?.valueKind === 'string' ? (
        <input
          aria-label={`Display filter value ${number}`}
          onChange={(event) => onChange({ ...condition, value: event.target.value })}
          type="text"
          value={typeof condition.value === 'string' ? condition.value : ''}
        />
      ) : null}
      <button
        aria-label={`Remove display filter condition ${number}`}
        onClick={onRemove}
        type="button"
      >
        ×
      </button>
    </div>
  );
}

export function DisplayFilterEditor({ fields, filter, layerId }: DisplayFilterEditorProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const canEdit = useCapability('data.displayFilter');
  const setDisplayFilter = useAppStore((state) => state.setLayerDisplayFilter);
  const usableFields = attributeRuleFields(fields);
  const conditions =
    filter?.children.filter(
      (child): child is AttributeRuleCondition => child.kind === 'condition',
    ) ?? [];
  const count = conditions.length;
  const summary = filter ? `${count} ${count === 1 ? 'condition' : 'conditions'}` : 'All features';

  const apply = (next: AttributeRuleGroup | null) => {
    const result = setDisplayFilter(layerId, next);
    setNotice(result.ok ? null : result.message);
  };
  const addCondition = () => {
    const condition = createDefaultAttributeRuleCondition(fields);
    if (!condition) return;
    apply({ kind: 'group', operator: 'and', children: [...conditions, condition] });
  };
  const updateCondition = (index: number, condition: AttributeRuleCondition) => {
    apply({
      kind: 'group',
      operator: 'and',
      children: conditions.map((candidate, candidateIndex) =>
        candidateIndex === index ? condition : candidate,
      ),
    });
  };
  const removeCondition = (index: number) => {
    const next = conditions.filter((_, candidateIndex) => candidateIndex !== index);
    apply(next.length === 0 ? null : { kind: 'group', operator: 'and', children: next });
  };

  return (
    <section className="inspector-section display-filter-section">
      <div className="display-filter-heading">
        <h3>Display filter</h3>
        <span>{summary}</span>
      </div>
      {!canEdit ? (
        <>
          {conditions.length > 0 ? (
            <ol
              className="display-filter-read-only"
              aria-label="Authored display filter conditions"
            >
              {conditions.map((condition, index) => (
                <li key={`${condition.field}:${condition.operator}:${index}`}>
                  {conditionSummary(condition, fields)}
                </li>
              ))}
            </ol>
          ) : null}
          <p className="style-help">
            Placeio Maps Pro is required to edit Display Filter. Authored filters continue to render
            and save.
          </p>
        </>
      ) : (
        <div className="display-filter-content">
          {conditions.length > 0 ? (
            <p className="labels-placement-note">Show features where all conditions match</p>
          ) : null}
          {conditions.map((condition, index) => (
            <ConditionEditor
              condition={condition}
              fields={fields}
              index={index}
              key={`${condition.field}:${index}`}
              onChange={(next) => updateCondition(index, next)}
              onRemove={() => removeCondition(index)}
            />
          ))}
          <div className="display-filter-actions">
            <button
              disabled={
                usableFields.length === 0 || conditions.length >= MAX_DISPLAY_FILTER_CONDITIONS
              }
              onClick={addCondition}
              type="button"
            >
              Add condition
            </button>
            {filter ? (
              <button onClick={() => apply(null)} type="button">
                Clear filter
              </button>
            ) : null}
          </div>
        </div>
      )}
      {notice ? (
        <p className="style-notice" role="status">
          {notice}
        </p>
      ) : null}
    </section>
  );
}
